/*
 * Copyright (c) 2020 NOOGA DEVELOPMENT, All rights reserved.
 * Author Raman Kremko <raman.kremko@noogadev.com>
 */

import apiClient from 'data/api/apiClient';
import {
    AlertsSearchParams,
    AlertsListDto,
    AlertItemsListDto,
    AlertDto,
    AlertItemDto,
    AlertItemsSearchParams,
    TemplatesListSearchParams,
    TemplatesList,
    AlertTemplateDTO,
    TemplateExecuteParameters,
    TemplateExecutionsSearchParams,
    TemplateExecutionListDTO,
    TEMPLATES_SORT_TYPES,
    CreateTemplateRulesParams,
    TemplateRulesListDto,
    TemplateRulesListSearchParams,
    UpdateTemplateRuleDto,
    AlertName,
    EntityType,
    TemplateRuleTaxNumbersSearchParams,
} from './alertsClient.typings';
import { arrayUniqueStr, cutEmptyParams } from '../../../utils/utils';

function getAlertsList(params: AlertsSearchParams): Promise<AlertsListDto> {
    const sortById = `id,${params.sort.split(',')[1]}`;
    (params as unknown as { sort: string[] }).sort = arrayUniqueStr([params.sort, sortById]);
    return apiClient.get<AlertsListDto>('1.0.0/alerts', params);
}

function getTemplates(params: TemplatesListSearchParams): Promise<TemplatesList> {
    params.sort = TEMPLATES_SORT_TYPES[1];
    return apiClient.get<TemplatesList>('1.0.0/alerts/templates', params);
}

function updateAlertTemplate(template: AlertTemplateDTO): Promise<AlertTemplateDTO> {
    return apiClient.post<AlertTemplateDTO>(`1.0.0/alerts/templates/${template.id}`, template);
}

function executeTemplate(id: number, params: TemplateExecuteParameters | {}): Promise<string> {
    return apiClient.post<string>(`1.0.0/alerts/templates/${id}/executions`, params);
}

function getExecutionsList(id: number, params: TemplateExecutionsSearchParams): Promise<TemplateExecutionListDTO> {
    return apiClient.get<TemplateExecutionListDTO>(`1.0.0/alerts/templates/${id}/executions`, params);
}

function getAlertItems(id: string, params: AlertItemsSearchParams): Promise<AlertItemsListDto> {
    return apiClient.get<AlertItemsListDto>(`1.0.0/alerts/${id}/items`, params);
}

function getAlertDetails(id: string): Promise<AlertDto> {
    return apiClient.get<AlertDto>(`1.0.0/alerts/${id}`);
}

function updateAlerts(alerts: AlertDto[]): Promise<AlertDto[]> {
    return apiClient.post<AlertDto[]>('1.0.0/alerts', alerts);
}

function updateAlertItem(alertItem: AlertItemDto): Promise<AlertItemDto> {
    return apiClient.put<AlertItemDto>(`1.0.0/alerts/${alertItem.alertId}/items/${alertItem.id}`, alertItem);
}

function loadAlertDataReportFile(alertId: AlertDto['id']): Promise<ArrayBuffer> {
    return apiClient.downloadFile(`1.0.0/alerts/${alertId}/excel`);
}

function getTemplateRules(
    templateName: AlertName,
    params: TemplateRulesListSearchParams,
): Promise<TemplateRulesListDto> {
    return apiClient.get<TemplateRulesListDto>(`1.0.0/alerts/types/${templateName}/rules`, cutEmptyParams(params));
}

function searchTaxNumbersByType(params: TemplateRuleTaxNumbersSearchParams): Promise<string[]> {
    return apiClient.post<string[]>('1.0.0/alerts/types/taxNumbersList', { ...params, limit: 20 });
}

function createTemplateRules(templateName: AlertName, params: CreateTemplateRulesParams): Promise<{}> {
    return apiClient.post(`1.0.0/alerts/types/${templateName}/rules`, params);
}

function updateTemplateRules(templateName: AlertName, ruleId: number, params: UpdateTemplateRuleDto): Promise<{}> {
    return apiClient.patch(`1.0.0/alerts/types/${templateName}/rules/${ruleId}`, params);
}

function deleteTemplateRules(templateName: AlertName, rulesIds: number[]): Promise<{}> {
    return apiClient.delete(`1.0.0/alerts/types/${templateName}/rules`, null, rulesIds);
}

async function downloadTemplateRulesAsExcel(
    templateName: AlertName,
    params: Partial<TemplateRulesListSearchParams>,
): Promise<ArrayBuffer> {
    return apiClient.downloadFile(`1.0.0/alerts/types/${templateName}/rules/excel`, cutEmptyParams(params));
}

async function downloadRulesSampleAsExcel(alertEntityType: EntityType): Promise<ArrayBuffer> {
    return apiClient.downloadFile(`1.0.0/alerts/types/${alertEntityType}/rules/sample`);
}

export const alertsClient = {
    getAlertsList,
    getAlertDetails,
    getAlertItems,
    updateAlertItem,
    updateAlerts,
    loadAlertDataReportFile,
    getTemplates,
    searchTaxNumbersByType,
    updateAlertTemplate,
    executeTemplate,
    getExecutionsList,
    getTemplateRules,
    createTemplateRules,
    updateTemplateRules,
    deleteTemplateRules,
    downloadTemplateRulesAsExcel,
    downloadRulesSampleAsExcel,
};
